import { useMemo } from "react";
import { type Organization, useOrganizations } from "@/lib/api";
import { getPublicBootstrapOrganization } from "@/lib/public-bootstrap";
import { useViewAsVisitor } from "@/lib/view-as";
import { useOrgContext } from "./use-org-context";

interface UseCurrentOrgResult {
	org: Organization | null;
	isMember: boolean;
	isPublic: boolean;
	isLoading: boolean;
}

/**
 * Resolves the Organization record for the org currently in the URL (via
 * useOrgContext), or an explicit sidebar workspace on account-wide pages,
 * against the list returned by `/api/organizations`. The
 * response includes both the caller's member orgs and any public orgs, so
 * callers can distinguish "I'm browsing a public workspace as a non-member"
 * from "I'm actually a member here".
 */
export function useCurrentOrg(owner?: string): UseCurrentOrgResult {
	const { session, urlOrgSlug } = useOrgContext();
	const { data: orgs, isLoading } = useOrganizations();
	const bootstrapOrg = getPublicBootstrapOrganization();
	// Signed-in visitor preview (`?view_as=visitor`): behave as a non-member.
	// Downgrade-only — the server still authorizes from the session.
	const viewAsVisitor = useViewAsVisitor();
	const slug = owner ?? urlOrgSlug;

	const org = useMemo(() => {
		if (!slug) return null;
		return (
			orgs?.find((o) => o.slug === slug) ??
			(bootstrapOrg?.slug === slug ? bootstrapOrg : null)
		);
	}, [bootstrapOrg, orgs, slug]);

	return {
		org,
		isMember: !!org?.is_member && !viewAsVisitor,
		isPublic: org?.visibility === "public",
		isLoading: session ? isLoading : isLoading && !org,
	};
}
